import * as _ from 'underscore';
import { Keys } from '../helpers/Keys';
import { Module } from '../Module';

/*
This is an internal implementation of an HTML Select component
*/

// Select

export interface ISelectOption {
  value: any;
  disabled: boolean;
  title?: string;
  transclude: ng.ITranscludeFunction;
  count?: number;
}

export class SelectController {

  // This is the last value selected.  Used to find a selection if the value is
  // set before the options or used to validate the current selection if the value changes
  private value: any;

  public selectedOption: ISelectOption;
  public options: ISelectOption[] = [];
  public noneOptionTransclude: ng.ITranscludeFunction;

  currentOptionTranscludeFn(): ng.ITranscludeFunction {
    if (this.selectedOption) {
      return this.selectedOption.transclude;
    } else {
      // Displayed when there is no currently selected option, whether options exist or not.
      // Useful for displaying calls to action.
      return this.noneOptionTransclude;
    }
  }

  select(value: any) {
    this.value = value;
    this.selectedOption = _.findWhere(this.options, { value: value });
  }

  selectOption(option: ISelectOption) {
    this.selectedOption = option;
    this.value = this.selectedOption.value;
  }

  selectPrevious() {
    const currentIndex = _.indexOf(this.options, this.selectedOption);

    let previousEnabledIndex = currentIndex - 1;

    // Skip over disabled options
    while(previousEnabledIndex >= 0 && this.options[previousEnabledIndex].disabled){
      previousEnabledIndex--;
    }

    if (previousEnabledIndex >= 0) {
      this.selectOption(this.options[previousEnabledIndex]);
    }
  }

  selectNext() {
    const currentIndex = _.indexOf(this.options, this.selectedOption);
    let nextEnabledIndex = currentIndex + 1;

    // Skip over disabled options
    while(nextEnabledIndex < this.options.length && this.options[nextEnabledIndex].disabled){
      nextEnabledIndex++;
    }

    if (nextEnabledIndex < this.options.length) {
      this.selectOption(this.options[nextEnabledIndex]);
    }
  }

  addOption(option: ISelectOption) {
    this.options.push(option);
    if(this.isOptionForValue(option, this.value)) this.selectedOption = option;
  }

  setNoneOptionTransclude(transclude: ng.ITranscludeFunction) {
    this.noneOptionTransclude = transclude;
  }

  removeOption(option: ISelectOption) {
    this.options = _(this.options).without(option);
    if(this.selectedOption === option) this.selectedOption = null;
  }

  verifySelection() {
    this.select(this.value);
  }

  private isOptionForValue(option: ISelectOption, value: any) {
    return _.isEqual(option.value, value);
  }

}

export interface SelectScope extends ng.IScope {
  selectCtrl: SelectController;
  maxCountDigits: number;
  contentClass: string;
  disabled: boolean;
  optionClicked: (option: ISelectOption) => void;
  keydown: ($event:JQueryKeyEventObject) => void;
}

Module.instance().directive('tbSelect', <any[]>[
  () => {
    return {
      restrict: 'E',
      replace: true,
      require: ['tbSelect', '?ngModel'],
      controllerAs: 'selectCtrl',
      transclude: true,
      templateUrl: 'select.html',
      scope: {
        contentClass: '@tbSelectContentClass',
        disabled: '=tbDisabled',
        matchButtonWidth: '@',
        autoExpand: '@?'
      },
      controller: SelectController,
      link: (scope: SelectScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes, ctrls: any[]) => {
        const selectCtrl: SelectController = ctrls[0];
        const modelCtrl: ng.INgModelController = ctrls[1];

        // Calculate the maximum number of 'count' digits we need
        scope.maxCountDigits = 0;
        scope.$watch(() => selectCtrl.options, () => {
          if (selectCtrl.options) {
            scope.maxCountDigits = _.max(_.map(selectCtrl.options, (option: ISelectOption) => {
              if (_.isNumber(option.count)) {
                return ('' + option.count).length;
              }
              return 0;
            }));
          }
        });

        // Model-View synchronization

        if(modelCtrl) {
          modelCtrl.$render = () => selectCtrl.select(modelCtrl.$viewValue);
        }

        scope.$watch(() => selectCtrl.selectedOption, (selectedOption: ISelectOption) => {
          if(selectedOption && modelCtrl) modelCtrl.$setViewValue(selectedOption.value);
          element.trigger('change');
        }, true);

        scope.$watch('selectCtrl.selectedOption.value', (val: any) => selectCtrl.verifySelection());


        // Mouse handling

        scope.optionClicked = (option: ISelectOption) => {
          selectCtrl.selectOption(option);
          element.focus();  // Return focus to tb:select when an option is clicked
        };

        // Keyboard handling

        // ... CONSIDER: this is ugly. Perhaps remove dependency on popup menu i.e. merge popup-menu
        //     into tb:select and/or add or move keyboard handling logic to popup menu.

        const popupMenuCtrl = () => {
          const elementScope = element.find('.tb-popup').scope() as any;
          const popupElementScope = elementScope;
          return popupElementScope.popupMenu;
        };

        scope.keydown = ($event: JQueryKeyEventObject) => {
          switch ($event.which) {
            case Keys.ArrowUp:
              selectCtrl.selectPrevious();
              break;

            case Keys.ArrowDown:
              selectCtrl.selectNext();
              break;

            case Keys.Tab:
            case Keys.Escape:
              if (!scope.disabled && popupMenuCtrl().isOpen()) {
                popupMenuCtrl().close();
                $event.preventDefault();   // Prevents Tab from moving to the next control
                $event.stopPropagation();  // Prevents Escape from closing containing dialog (if any)
              }
              break;

            // Otherwise, delegate the keydown event to the popupMenuCtrl
            default:
              popupMenuCtrl().keydown($event);
              break;
          }
        };

      }
    };
  }
]);

// Select

interface SelectOptionsAttributes extends ng.IAttributes {
  value: string;
}

interface SelectOptionsScope extends ng.IScope {
  value: any;
  disabled: boolean;
  title?: string;
  count?: number;
}

Module.instance().directive('tbSelectOption', [
  () => {
    return {
      restrict: 'E',
      replace: true,
      transclude: true,
      require: '^tbSelect',
      scope: {
        value: '=',
        count: '=',
        disabled: '=tbDisabled',
        title: '='
      },
      link: (scope: SelectOptionsScope, element: JQuery, attrs: SelectOptionsAttributes, selectCtrl: SelectController, transcludeFn: ng.ITranscludeFunction) => {
        const option: ISelectOption = {
          value: scope.value,
          disabled: scope.disabled,
          title: scope.title,
          transclude: transcludeFn,
          count: scope.count
        };

        scope.$watch(() => scope.value, () => {
          option.value = scope.value;
        });

        element.on('$destroy', () => selectCtrl.removeOption(option));

        selectCtrl.addOption(option);
      }
    };
  }
]);

Module.instance().directive('tbSelectNone', ['$parse',
  ($parse: ng.IParseService) => {
    return {
      restrict: 'E',
      replace: true,
      transclude: true,
      require: '^tbSelect',
      scope: {},
      link: (scope:ng.IScope, element:JQuery, attrs:SelectOptionsAttributes, selectCtrl:SelectController, transcludeFn:ng.ITranscludeFunction) => {
        selectCtrl.setNoneOptionTransclude(transcludeFn);
      }
    };
  }
]);
